import { useEffect, useState } from "react";

import { PlusCircleOutlined } from "@ant-design/icons";
import { FormInstance } from "antd";

import { RVButton } from "@regal-voice/ui";
import { Variant } from "Types/Journey";

import { VariantFormRow } from "./VariantsFormRow";

import styles from "./VariantsFormItem.module.scss";

export function VariantsFormItems({
    onChange,
    form,
    formValues,
    setCanSubmit,
}: {
    onChange?: (e: {
        variant1: Variant;
        variant2: Variant;
        variant3: Variant;
        variant4: Variant;
        variant5: Variant;
    }) => void;
    form: FormInstance<any>;
    formValues: any;
    setCanSubmit: (b: boolean) => void;
}): JSX.Element {
    // we could include the value prop, value?: Record<string, Variant>; in a future PR ,
    // instead of formValues - not required for this PR formValues and form.getFieldsValue()
    // creates high coupling with Form but i'm pretty sure this component wont be used in another place

    const [activeVariantCount, setActiveVariantCount] = useState<number>(2);
    const { variant1, variant2, variant3, variant4, variant5 } = formValues?.variants || {};
    const [weightError, setWeightError] = useState<string>();
    const [nameErrors, setNameErrors] = useState<Record<string, string | undefined>>({
        variant1: undefined,
        variant2: undefined,
        variant3: undefined,
        variant4: undefined,
        variant5: undefined,
    });

    const [variants, setVariants] = useState<Array<Variant>>([
        !!variant1 ? { id: 1, isActive: true, ...variant1 } : { id: 1, isActive: true, weight: 50 },
        !!variant2 ? { id: 2, isActive: true, ...variant2 } : { id: 2, isActive: true, weight: 50 },
        !!variant3 ? { id: 3, isActive: true, ...variant3 } : { id: 3, isActive: false },
        !!variant4 ? { id: 4, isActive: true, ...variant4 } : { id: 4, isActive: false },
        !!variant5 ? { id: 5, isActive: true, ...variant5 } : { id: 5, isActive: false },
    ]);

    useEffect(() => {
        const activeVariants = Object.entries(formValues?.variants || {}).filter(([_, v]) => (v as Variant).isActive);
        // these is a strange issue where this occasionally is undefined on first render
        // but it can never be less than 2 so we will fall back to maintain the correct display
        setActiveVariantCount(activeVariants.length || 2);
    }, [formValues]);

    useEffect(() => {
        onChange?.({
            variant1: variants[0],
            variant2: variants[1],
            variant3: variants[2],
            variant4: variants[3],
            variant5: variants[4],
        });
    }, [variants]);

    useEffect(() => {
        updateCanSubmit();
    }, [weightError, nameErrors, variants]);

    function updateCanSubmit() {
        const { variants: fieldValues } = form.getFieldsValue();
        const noNameErrors: boolean = Object.values(nameErrors)?.filter((e) => e != undefined)?.length === 0;
        const variantNamesFilled: boolean = !!fieldValues
            ? Object.values(fieldValues)
                  .filter((v) => (v as Variant).isActive)
                  .map((v) => !!(v as Variant).name)
                  .reduce((prev, cur) => prev && cur, true)
            : false;

        setCanSubmit(!weightError && noNameErrors && variantNamesFilled);
    }

    function calculateVariantWeights(len: number): Array<number> {
        // handle special case for 3 variants
        return len === 3 ? [33, 33, 34] : Array.from({ length: len }, () => 100 / len);
    }

    const sumActiveWeights = (prev: any, curr: any) => prev + (curr.isActive ? Number(curr.weight) || 0 : 0);

    function onWeightChange() {
        const { variants: fieldValues } = form.getFieldsValue();
        const totalWeight = Object.values(fieldValues).reduce(sumActiveWeights, 0);

        const weightMessage = totalWeight != 100 ? `Variants must add to 100%, not ${totalWeight}%.` : undefined;

        setWeightError(weightMessage);
    }

    function onNameChange(id: number) {
        const { variants: fieldValues } = form.getFieldsValue();
        const updatedVariant = `variant${id}`;
        const currentError = nameErrors[updatedVariant];
        const newFieldValue = fieldValues[updatedVariant]?.name;

        if (!newFieldValue) {
            setNameErrors({
                ...nameErrors,
                [updatedVariant]: "Variant name is required.",
            });
        }

        if (!!currentError && !!newFieldValue) {
            setNameErrors({
                ...nameErrors,
                [updatedVariant]: undefined,
            });
        }

        updateCanSubmit();
    }

    function addVariant() {
        if (activeVariantCount < 5) {
            const variantWeights = calculateVariantWeights(activeVariantCount + 1);

            const { variants: fieldValues } = form.getFieldsValue();
            const currentFieldValues: Array<Variant> = Object.values(fieldValues);

            setCanSubmit(false);

            const newVariants = [
                // reset current field weights to new evenly split weights
                ...variants.slice(0, activeVariantCount).map((v, vIndex) => {
                    return {
                        ...currentFieldValues[vIndex],
                        weight: variantWeights[vIndex],
                    };
                }),
                // add a new variant
                {
                    ...variants[activeVariantCount],
                    name: undefined,
                    weight: variantWeights[activeVariantCount],
                    isActive: true,
                    previousId: undefined,
                },
                // inactive but not deleted variants still included in state
                ...variants.slice(activeVariantCount + 1),
            ];
            setVariants(newVariants);
        }
    }

    function removeVariant(id: any) {
        if (activeVariantCount > 2 && id > 2) {
            const variantWeights = calculateVariantWeights(activeVariantCount - 1);

            const { variants: fieldValues } = form.getFieldsValue();
            const currentFieldValues: Array<Variant> = Object.values(fieldValues);

            setNameErrors({
                ...nameErrors,
                [`variant${id}`]: undefined,
            });

            setVariants([
                ...variants.slice(0, id - 1).map((v, vIndex) => {
                    return {
                        ...v,
                        name: currentFieldValues[vIndex]?.name,
                        weight: variantWeights[vIndex],
                        previousId: undefined,
                    };
                }),
                ...variants.slice(id).map((v, vIndex) => {
                    return {
                        ...v,
                        name: currentFieldValues[vIndex + id]?.name,
                        id: v.id - 1,
                        previousId: v.id,
                        weight: variantWeights[variantWeights.length - 1],
                    };
                }),
                { id: 5, isActive: false },
            ]);
        }
    }

    return (
        <>
            {variants?.map((variant) => {
                const nameError = nameErrors[`variant${variant.id}`];
                const displayVariantRow = formValues?.variants?.[`variant${variant.id}`]?.isActive || variant.id < 3;
                if (displayVariantRow) {
                    return (
                        <>
                            <VariantFormRow
                                key={`variantFormRow${variant.id}-form-row`}
                                variant={variant}
                                onRemove={removeVariant}
                                canRemove={variant.id > 2}
                                onNameChange={onNameChange}
                                onWeightChange={onWeightChange}
                                nameError={!!nameError}
                                weightError={!!weightError}
                            />
                            {nameErrors[`variant${variant.id}`] && (
                                <span key={`variant${variant.id}-name-error`} className={styles.formError}>
                                    Variant Name is required.
                                </span>
                            )}
                        </>
                    );
                }
            })}
            <RVButton
                disabled={activeVariantCount === 5}
                style={{ marginTop: "10px", width: "50%" }}
                onClick={() => {
                    addVariant();
                }}
            >
                <PlusCircleOutlined />
                <span style={{ marginLeft: "5px" }}>Add Variant</span>
            </RVButton>

            {!!weightError && <span className={styles.formError}>{weightError}</span>}
        </>
    );
}
